'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetStudentMentoringStateOutputDTO } from '@algoacademy/backend/modules/payment/endpoints/queries/get-student-mentoring-state/output.dto';
import { Link } from '@algoacademy/design-system/web/actions/Link';
import { DarkCard, LightCard } from '@algoacademy/design-system/web/data-display/Card';
import { DescriptionDetails } from '@algoacademy/design-system/web/data-display/DescriptionList/DescriptionDetails';
import { DescriptionList } from '@algoacademy/design-system/web/data-display/DescriptionList/DescriptionList';
import { DescriptionTerm } from '@algoacademy/design-system/web/data-display/DescriptionList/DescriptionTerm';
import { H4 } from '@algoacademy/design-system/web/typography/Heading';
import { P2, P3 } from '@algoacademy/design-system/web/typography/Paragraph';
import { formatWarsawLongDate } from '@algoacademy/shared/warsaw-calendar';
import { formatDiscountSummary, formatGrosze, formatTermsSummary } from '../../_helpers/mentoring-terms';

/**
 * The CRM has no address for a single contact - contacts open in a drawer over
 * the list - so staff lands on the list and searches the buyer's e-mail, which
 * the panel prints right next to the link.
 */
const CRM_CONTACTS_HREF = '/crm/contacts';
type StudentSubscriptionPanelProps = {
  subscriptionState: GetStudentMentoringStateOutputDTO['subscriptionState'];
  current: NonNullable<GetStudentMentoringStateOutputDTO['current']>;
  scheduledChange: GetStudentMentoringStateOutputDTO['scheduledChange'];
};

/**
 * What this student is on right now, in the words the backend already chose:
 * mentoring, cadence, hours, who pays, when it renews - plus the change waiting
 * for that renewal, when one waits. Read-only; every state renders it, because
 * staff needs the same facts whether or not a change is possible.
 */
export function StudentSubscriptionPanel(t0) {
  const $ = _c(47);
  const {
    subscriptionState,
    current,
    scheduledChange
  } = t0;
  const renewalLabel = subscriptionState === "cancelled" ? "Ko\u0144czy si\u0119" : "Odnawia si\u0119";
  let t1;
  if ($[0] !== current.billingCadenceLabel || $[1] !== current.discount) {
    t1 = current.discount === null ? null : formatDiscountSummary(current.discount, current.billingCadenceLabel);
    $[0] = current.billingCadenceLabel;
    $[1] = current.discount;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const discountInForceSummary = t1;
  const scheduledDiscount = scheduledChange === null ? null : scheduledChange.discount;
  let t2;
  if ($[3] !== scheduledChange || $[4] !== scheduledDiscount) {
    t2 = scheduledChange === null || scheduledDiscount === null ? null : formatDiscountSummary(scheduledDiscount, scheduledChange.billingCadenceLabel);
    $[3] = scheduledChange;
    $[4] = scheduledDiscount;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const scheduledDiscountSummary = t2;
  const hasDiscountChange = scheduledChange !== null && scheduledDiscountSummary !== discountInForceSummary;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <H4>Aktualna subskrypcja ucznia</H4>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DescriptionTerm>Mentoring</DescriptionTerm>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== current.billingCadenceLabel || $[9] !== current.hoursWithMentor || $[10] !== current.mentoringTypeLabel) {
    t5 = formatTermsSummary({
      mentoringTypeLabel: current.mentoringTypeLabel,
      billingCadenceLabel: current.billingCadenceLabel,
      hoursWithMentor: current.hoursWithMentor
    });
    $[8] = current.billingCadenceLabel;
    $[9] = current.hoursWithMentor;
    $[10] = current.mentoringTypeLabel;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <DescriptionDetails>{t5}</DescriptionDetails>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== discountInForceSummary) {
    t7 = discountInForceSummary === null ? null : <><DescriptionTerm>Zniżka</DescriptionTerm><DescriptionDetails>{discountInForceSummary}</DescriptionDetails></>;
    $[14] = discountInForceSummary;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DescriptionTerm>Uczeń</DescriptionTerm>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== current.studentName) {
    t9 = <DescriptionDetails>{current.studentName}</DescriptionDetails>;
    $[17] = current.studentName;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DescriptionTerm>Kupujący</DescriptionTerm>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== current.buyerEmail || $[21] !== current.buyerName) {
    t11 = <DescriptionDetails>{current.buyerName} ({current.buyerEmail})</DescriptionDetails>;
    $[20] = current.buyerEmail;
    $[21] = current.buyerName;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== renewalLabel) {
    t12 = <DescriptionTerm>{renewalLabel}</DescriptionTerm>;
    $[23] = renewalLabel;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== current.renewsAt) {
    t13 = formatWarsawLongDate(current.renewsAt);
    $[25] = current.renewsAt;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = <DescriptionDetails>{t13}</DescriptionDetails>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t11 || $[30] !== t12 || $[31] !== t14 || $[32] !== t6 || $[33] !== t7 || $[34] !== t9) {
    t15 = <DescriptionList>{t4}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t14}</DescriptionList>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t14;
    $[32] = t6;
    $[33] = t7;
    $[34] = t9;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== hasDiscountChange || $[37] !== scheduledChange || $[38] !== scheduledDiscountSummary) {
    t16 = scheduledChange === null ? null : <LightCard className="flex flex-col gap-2 p-3"><P2 className="font-medium">Zaplanowana zmiana</P2><P3 className="text-contentSecondary">Od {formatWarsawLongDate(scheduledChange.effectiveAt)} mentoring zmienia się na:{" "}{formatTermsSummary({
          mentoringTypeLabel: scheduledChange.mentoringTypeLabel,
          billingCadenceLabel: scheduledChange.billingCadenceLabel.toLowerCase(),
          hoursWithMentor: scheduledChange.hoursWithMentor
        })}. Cena odnowienia: {formatGrosze(scheduledChange.priceGrosze)} - w cenie 23% VAT. Do tej daty nic się nie zmienia.</P3>{hasDiscountChange ? <P3 className="text-contentSecondary">{scheduledDiscountSummary === null ? "Od tej daty zni\u017Cka przestaje dzia\u0142a\u0107 - ucze\u0144 p\u0142aci pe\u0142n\u0105 cen\u0119." : `Zniżka od tej daty: ${scheduledDiscountSummary}`}</P3> : null}</LightCard>;
    $[36] = hasDiscountChange;
    $[37] = scheduledChange;
    $[38] = scheduledDiscountSummary;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Link href={CRM_CONTACTS_HREF} target="_blank" rel="noreferrer" className="text-algoprimary underline">otwórz listę kontaktów</Link>;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== current.buyerEmail) {
    t18 = <P3 className="text-contentSecondary">Historia zmian jest na kontakcie kupującego w CRM -{" "}{t17}{" "}i wyszukaj {current.buyerEmail}.</P3>;
    $[41] = current.buyerEmail;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t15 || $[44] !== t16 || $[45] !== t18) {
    t19 = <DarkCard className="flex flex-col gap-3 p-4">{t3}{t15}{t16}{t18}</DarkCard>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
}